// EditableShape adapter for SchemeOutline (property boundary)
// Translates uniform tool interface into GardenViewer.setSchemeOutline() calls
// Boundary uses absolute points (outline.x + point.x), while EditableShape
// returns center-relative points — this adapter converts transparently.
//
// Rotation is unified with garden.rotation (north direction).
// Rotating the boundary = rotating the garden's north orientation.

import type { EditableShape } from '@/types/editor'
import type { SchemeOutline } from '@/types/garden'
import { undoStack } from '../UndoStack'
import { debug } from '@/utils/debug'

export class BoundaryAdapter implements EditableShape {
  readonly sourceType = 'boundary' as const
  readonly id = 'boundary'

  private getOutline: () => SchemeOutline | undefined
  private setOutline: (outline: SchemeOutline) => void
  private getGardenRotation: () => number
  private setGardenRotation: (deg: number) => void

  /** Snapshot taken at beginEdit(), used to compute undo delta */
  private snapshot: {
    x: number; y: number;
    points: Array<{ x: number; y: number }>;
    gardenRotation: number;
  } | null = null

  constructor(
    getOutline: () => SchemeOutline | undefined,
    setOutline: (outline: SchemeOutline) => void,
    getGardenRotation: () => number,
    setGardenRotation: (deg: number) => void,
  ) {
    this.getOutline = getOutline
    this.setOutline = setOutline
    this.getGardenRotation = getGardenRotation
    this.setGardenRotation = setGardenRotation
  }

  private requireOutline(): SchemeOutline {
    const outline = this.getOutline()
    if (!outline) throw new Error('BoundaryAdapter: outline not found')
    return outline
  }

  // --- Read geometry (editor coords, dots) ---

  /** Centroid of absolute points */
  getCenter(): { x: number; y: number } {
    const outline = this.requireOutline()
    if (outline.points.length === 0) return { x: outline.x, y: outline.y }

    let cx = 0, cy = 0
    for (const p of outline.points) {
      cx += outline.x + p.x
      cy += outline.y + p.y
    }
    return { x: cx / outline.points.length, y: cy / outline.points.length }
  }

  getSize(): { width: number; height: number } {
    const outline = this.requireOutline()
    if (outline.points.length === 0) return { width: 0, height: 0 }

    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity
    for (const p of outline.points) {
      const ax = outline.x + p.x
      const ay = outline.y + p.y
      if (ax < minX) minX = ax
      if (ax > maxX) maxX = ax
      if (ay < minY) minY = ay
      if (ay > maxY) maxY = ay
    }
    return { width: maxX - minX, height: maxY - minY }
  }

  /** Returns garden.rotation — boundary rotation IS the garden's north orientation */
  getRotation(): number {
    return this.getGardenRotation()
  }

  getShapeKind(): 'rectangle' | 'ellipse' | 'polygon' {
    return 'polygon'
  }

  /** Returns center-relative points (absolute → subtract centroid) */
  getPoints(): Array<{ x: number; y: number }> | null {
    const outline = this.requireOutline()
    if (outline.points.length < 3) return null

    const center = this.getCenter()
    return outline.points.map(p => ({
      x: (outline.x + p.x) - center.x,
      y: (outline.y + p.y) - center.y,
    }))
  }

  // --- Write geometry (editor coords, dots) ---

  /** Move boundary by shifting outline.x/y (points stay relative) */
  moveTo(x: number, y: number): void {
    const outline = this.requireOutline()
    const center = this.getCenter()
    const dx = x - center.x
    const dy = y - center.y
    this.setOutline({
      x: outline.x + dx,
      y: outline.y + dy,
      points: outline.points.map(p => ({ ...p })),
    })
  }

  resize(_width: number, _height: number): void {
    // No-op: boundary is not resizable via corner handles
  }

  /**
   * Rotate boundary to `degrees` (absolute, matching garden.rotation).
   * Computes delta from snapshot's garden.rotation, rotates points around centroid,
   * and updates garden.rotation + sun/compass direction.
   */
  rotate(degrees: number): void {
    if (!this.snapshot) return // Only works during an edit session

    // Delta from snapshot rotation to target rotation
    const delta = degrees - this.snapshot.gardenRotation

    // Convert snapshot to absolute points
    const absPoints = this.snapshot.points.map(p => ({
      x: this.snapshot!.x + p.x,
      y: this.snapshot!.y + p.y,
    }))

    // Compute centroid of snapshot points
    let cx = 0, cy = 0
    for (const p of absPoints) { cx += p.x; cy += p.y }
    cx /= absPoints.length
    cy /= absPoints.length

    // Rotate all points around centroid by delta
    const rad = delta * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    const rotated = absPoints.map(p => {
      const dx = p.x - cx
      const dy = p.y - cy
      return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos }
    })

    // Rotate origin around centroid
    const odx = this.snapshot.x - cx
    const ody = this.snapshot.y - cy
    const newX = cx + odx * cos - ody * sin
    const newY = cy + odx * sin + ody * cos

    this.setOutline({
      x: newX,
      y: newY,
      points: rotated.map(p => ({ x: p.x - newX, y: p.y - newY })),
    })

    // Update garden rotation (= north direction) and sun/compass
    this.setGardenRotation(((degrees % 360) + 360) % 360)
  }

  /** Set points in center-relative coords → convert to absolute (subtract centroid offset) */
  setPoints(relPoints: Array<{ x: number; y: number }>): void {
    const center = this.getCenter()
    // Convert center-relative back to outline-relative
    // absolute = center + relPoint, outline-relative = absolute - outline.x/y
    const outline = this.requireOutline()
    this.setOutline({
      x: outline.x,
      y: outline.y,
      points: relPoints.map(p => ({
        x: (center.x + p.x) - outline.x,
        y: (center.y + p.y) - outline.y,
      })),
    })
  }

  // --- Capabilities ---

  isResizable(): boolean {
    return false
  }

  // --- Visual ---

  getAccentColor(): number {
    return 0xff0000 // Red, matching outline tube color
  }

  // --- Undo integration ---

  beginEdit(): void {
    const outline = this.requireOutline()
    this.snapshot = {
      x: outline.x,
      y: outline.y,
      points: outline.points.map(p => ({ ...p })),
      gardenRotation: this.getGardenRotation(),
    }
  }

  commit(): void {
    if (!this.snapshot) return

    const before = this.snapshot
    const outline = this.requireOutline()
    const afterRotation = this.getGardenRotation()
    const after = {
      x: outline.x,
      y: outline.y,
      points: outline.points.map(p => ({ ...p })),
      gardenRotation: afterRotation,
    }

    // Skip if nothing changed
    const pointsChanged = JSON.stringify(before.points) !== JSON.stringify(after.points)
    const rotChanged = before.gardenRotation !== after.gardenRotation
    if (before.x === after.x && before.y === after.y && !pointsChanged && !rotChanged) {
      this.snapshot = null
      return
    }

    const setOutline = this.setOutline
    const setGardenRotation = this.setGardenRotation
    undoStack.push({
      description: 'Edit boundary',
      undo: () => {
        setOutline({ x: before.x, y: before.y, points: before.points.map(p => ({ ...p })) })
        setGardenRotation(before.gardenRotation)
      },
      redo: () => {
        setOutline({ x: after.x, y: after.y, points: after.points.map(p => ({ ...p })) })
        setGardenRotation(after.gardenRotation)
      },
    })

    debug.log('BoundaryAdapter', 'Committed edit')
    this.snapshot = null
  }

  cancel(): void {
    if (!this.snapshot) return
    this.setOutline({
      x: this.snapshot.x,
      y: this.snapshot.y,
      points: this.snapshot.points.map(p => ({ ...p })),
    })
    this.setGardenRotation(this.snapshot.gardenRotation)
    debug.log('BoundaryAdapter', 'Cancelled edit')
    this.snapshot = null
  }
}
